import { createContext, useContext } from 'react'

import type { Manifest } from '../api/authoring'

/**
 * The tenant a generated form is being drawn inside, and that tenant's
 * manifest.
 *
 * **Ambient rather than threaded, and only because a control needs it.**
 * `SchemaForm` draws what a schema says and nothing else, which is what
 * lets it serve a type this client has never heard of. A control that
 * has to *offer* something — the components a `x-component-role` field
 * may name — needs a fact the schema cannot carry, because "which
 * components exist" is not a property of the shape. Threading it through
 * every form, section and property would put that fact in the signature
 * of code that does not use it.
 *
 * The default is empty, and a control reading it degrades rather than
 * throws: a form rendered outside a scope offers nothing to pick and
 * says so, which is the same honest degradation an unrecognised format
 * already gets.
 */
export interface AuthoringScope {
  tenant: string
  manifest: Manifest | undefined
  /**
   * The type whose intent the form is filling, where a form is filling
   * one. A payload member marked `x-edge` names an edge by name, and only
   * the holder's declaration says what that edge accepts.
   */
  holder?: string
}

const ScopeContext = createContext<AuthoringScope>({ tenant: '', manifest: undefined })

export const AuthoringScopeProvider = ScopeContext.Provider

export function useAuthoringScope(): AuthoringScope {
  return useContext(ScopeContext)
}
